import { Injectable, inject, effect } from '@angular/core';
import { CartPersistenceService } from './cart-persistence.service';
import { CartStateService } from './cart-state.service';

/**
 * Restauration du panier au démarrage et persistance à chaque changement.
 *
 * Sorti de `CartService`, au-delà de la limite de 500 lignes. Les deux
 * méthodes sont appelées depuis son constructeur : `effect()` exige un
 * contexte d'injection.
 */
@Injectable({
    providedIn: 'root'
})
export class CartRestoreService {
    private persistence = inject(CartPersistenceService);
    private state = inject(CartStateService);
    private restoringFromDb = false;

    /**
     * Restore cart from IndexedDB on startup.
     *
     * @param rafraichirTarification rappelée après une restauration non vide, pour
     *   obtenir des taxes et des prix frais du backend.
     */
    demarrerRestauration(rafraichirTarification: () => void): void {
        this.persistence.init().then(() => this.restoreCart(rafraichirTarification));
    }

    /** Auto-persist on any cart state change. */
    persisterAChaqueChangement(): void {
        effect(() => {
            const items = this.state.items();
            const discount = this.state.discount();
            const customer = this.state.selectedCustomer();
            const taxes = this.state.taxes();

            if (this.restoringFromDb) return;

            this.persistence.save({
                items, discount, customer, taxes,
                savedAt: Date.now()
            });
        });
    }

    private async restoreCart(rafraichirTarification: () => void) {
        try {
            const state = await this.persistence.load();
            if (!state || !state.items?.length) return;

            // Expire after 24h
            if (Date.now() - state.savedAt > 24 * 60 * 60 * 1000) {
                await this.persistence.clear();
                return;
            }

            this.restoringFromDb = true;
            this.state.items.set(state.items);
            this.state.discount.set(state.discount || 0);
            if (state.customer) this.state.selectedCustomer.set(state.customer);
            if (state.taxes?.length) this.state.taxes.set(state.taxes);
            this.restoringFromDb = false;

            // Refresh pricing from backend to get fresh taxes/prices
            if (this.state.items().length > 0) {
                rafraichirTarification();
            }
        } catch (err) {
            console.error('[CartService] Restore failed:', err);
            this.restoringFromDb = false;
        }
    }
}
